import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MessageCircle, BrainCircuit, Users, Radio, Shield, BarChart3, Lock } from 'lucide-react';
import { cn, getRoomPath } from '../utils';
import { getTheme } from '../utils/themes';

const RoomCard = ({ room, featured = false }) => {
  if (!room) return null;
  const theme = getTheme(room.type);
  const isUpcoming = room.status?.toLowerCase() === 'upcoming';
  
  const participantsCount = room.participants_count || room.participants || 0;
  const hostName = room.host || room.owner?.name || room.user?.name || 'Anonymous';
  const isPrivate = room.privacy === 'private' || room.settings?.privacy === 'private' || room.has_password || room.settings?.password;

  if (featured) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        whileHover={{ y: -5 }}
        transition={{ duration: 0.3 }}
      >
        <Link to={getRoomPath(room)} className="block bg-white p-6 rounded-3xl border border-gray-100 hover:shadow-2xl hover:shadow-brand-indigo/10 transition-all duration-300 group cursor-pointer relative overflow-hidden h-full flex flex-col shadow-sm">
          <div className={cn("absolute top-0 left-0 w-1.5 h-full", theme.bg)}></div>
          
          {isPrivate && (
            <div className="absolute top-4 left-6 z-10">
              <div className="bg-white/90 backdrop-blur-sm p-1.5 rounded-lg shadow-sm border border-gray-100 text-gray-400">
                <Lock size={12} />
              </div>
            </div>
          )}
          
          <div className="flex justify-between items-start mb-6">
            <span className={cn("text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 rounded-full text-white flex items-center gap-2 shadow-lg", theme.bg)}>
              <span className="w-2 h-2 bg-white rounded-full animate-pulse shadow-[0_0_8px_rgba(255,255,255,0.8)]"></span>
              {room.status || 'LIVE'}
            </span>
            <div className="flex -space-x-2">
              {[1, 2, 3].map(j => (
                <div key={j} className="w-8 h-8 rounded-full border-2 border-white bg-gray-100 overflow-hidden shadow-sm">
                  <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${j + room.id}`} alt="avatar" />
                </div>
              ))}
            </div>
          </div>
          
          <h3 className="text-xl font-bold text-gray-900 group-hover:text-brand-indigo transition-colors line-clamp-2 leading-tight mb-2 tracking-tight">
            {room.title}
          </h3>
          <p className="text-[10px] text-gray-400 flex items-center gap-1.5 font-bold uppercase tracking-[0.15em] mb-8 opacity-80">
            <span className="w-1 h-1 rounded-full bg-gray-300"></span>
            {hostName}
          </p>

          <div className="flex items-center justify-between mt-auto pt-6 border-t border-gray-50">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5 text-gray-500">
                <Users size={14} className="text-gray-400" />
                <span className="text-[10px] font-bold uppercase tracking-[0.15em]">{participantsCount}</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-500">
                {room.type === 'Q&A' && <MessageCircle size={14} className="text-gray-400" />}
                {(room.type === 'Brainstorm' || room.type === 'Brainstorming') && <BrainCircuit size={14} className="text-gray-400" />}
                {room.type === 'Poll' && <BarChart3 size={14} className="text-gray-400" />}
                {room.type === 'Mixed' && <Shield size={14} className="text-gray-400" />}
                <span className="text-[10px] font-bold uppercase tracking-[0.15em]">{room.type}</span>
              </div>
            </div>
            <div className={cn("p-2 bg-gray-50 text-gray-400 rounded-xl group-hover:text-white transition-all duration-300 shadow-sm", `group-hover:${theme.bg}`)}>
              <Radio size={18} />
            </div>
          </div>
        </Link>
      </motion.div>
    );
  }

  return (
    <motion.div 
      whileHover={{ y: -4 }}
      className="bg-white p-5 rounded-3xl border border-gray-100 flex flex-col justify-between group cursor-pointer hover:shadow-xl hover:shadow-gray-100/50 hover:border-brand-indigo/20 transition-all min-h-[160px]"
    >
      <Link to={getRoomPath(room)} className="flex-1 flex flex-col">
        <div className="flex items-start justify-between mb-4">
          <div className={cn(
            "w-12 h-12 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-110",
            theme.lightBg,
            theme.text
          )}>
            {room.type === 'Q&A' ? <MessageCircle size={24} /> : 
             (room.type === 'Brainstorm' || room.type === 'Brainstorming') ? <BrainCircuit size={24} /> : 
             room.type === 'Poll' ? <BarChart3 size={24} /> : 
             room.type === 'Mixed' ? <Shield size={24} /> : <Radio size={24} />}
          </div>
          {room.is_featured && (
            <span className="bg-amber-50 text-amber-600 text-[9px] font-bold px-2 py-1 rounded-lg uppercase tracking-[0.15em] border border-amber-100 flex items-center gap-1">
              <Shield size={10} />
              Featured
            </span>
          )}
        </div>

        <h3 className="font-bold text-gray-900 group-hover:text-brand-indigo transition-colors line-clamp-1 mb-1 text-base tracking-tight">{room.title}</h3>
        <p className="text-xs text-gray-500 font-medium line-clamp-2 mb-4 flex-1">{room.description || "Join this interactive session and participate in the community discussion."}</p>
        
        <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-50">
          <div className="flex items-center gap-2">
            <span className={cn(
              "text-[10px] font-bold uppercase tracking-[0.15em] flex items-center gap-1.5",
              isUpcoming ? "text-amber-500" : "text-red-500"
            )}>
              <span className={cn(
                "w-1.5 h-1.5 rounded-full",
                isUpcoming ? "bg-amber-500" : "bg-red-500 animate-pulse"
              )}></span>
              {isUpcoming ? 'Upcoming' : 'Live'}
            </span>
            <span className="w-1 h-1 bg-gray-200 rounded-full"></span>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em]">
              {isUpcoming ? (room.scheduled_at ? new Date(room.scheduled_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'Scheduled') : `${participantsCount} active`}
            </span>
          </div>
          
          <div className="flex -space-x-2">
            {[1, 2, 3].map(j => (
              <div key={j} className="w-6 h-6 rounded-full border-2 border-white bg-gray-50 overflow-hidden shadow-sm">
                <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${j + room.id}`} alt="avatar" />
              </div>
            ))}
          </div>

          {isPrivate && (
            <div className="absolute top-4 right-6 text-gray-300">
               <Lock size={14} />
            </div>
          )}
        </div>
      </Link>
    </motion.div>
  );
};

export default RoomCard;
